import React, { useState, useContext, useMemo, useEffect } from "react";
import { Card, Typography, List, Button, Input, Tag, Pagination, Spin, message } from "antd";
import {
  EyeOutlined,
  CalendarOutlined,
  SearchOutlined,
  FileTextOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../../context/ThemeContext";
import apiClient from "../../services/apiClient";
import "./ModernNewsDashboard.css";

const { Title, Text, Paragraph } = Typography;

function parseDate(dateStr) {
  // Handles YYYY-MM-DD or similar
  return new Date(dateStr);
}

const placeholderImg =
  "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=600&q=80";

const ModernNewsDashboard = ({ onSelectNews }) => {
  const { isDark } = useContext(ThemeContext);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newsData, setNewsData] = useState({
    content: [],
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0,
    pageSize: 10
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Fetch news data from API
  const fetchNews = async (page = 1, size = 10) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${baseUrl}/news/approved?page=${page - 1}&size=${size}`);

      if (response.data.statusCode === 200) {
        setNewsData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch news');
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch news');
      message.error('Failed to fetch news. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchNews(currentPage, pageSize);
  }, []);

  // Handle page change
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    fetchNews(page, size);
  };

  // Handle search
  const handleSearch = (value) => {
    setSearch(value);
  };

  // Filter news based on search
  const filteredNews = useMemo(() => {
    if (!search.trim()) {
      return newsData.content;
    }

    return newsData.content.filter(
      (item) =>
        item.title?.toLowerCase().includes(search.toLowerCase()) ||
        item.company?.toLowerCase().includes(search.toLowerCase()) ||
        item.description?.toLowerCase().includes(search.toLowerCase())
    );
  }, [newsData.content, search]);

  const hero = filteredNews[0];
  const featured = filteredNews.slice(1, 4);
  const rest = filteredNews.slice(4);

  // Refresh data
  const handleRefresh = () => {
    fetchNews(currentPage, pageSize);
  };

  if (error && !loading) {
    return (
      <div className="modern-news-root">
        <div className="news-header-gradient">
          <Title level={1} className="news-hero-title">
            <FileTextOutlined /> Market News
          </Title>
          <Paragraph className="news-hero-subtitle">
            Stay updated with the latest financial and business news
          </Paragraph>
        </div>
        <div style={{ textAlign: 'center', padding: '50px 20px' }}>
          <Text type="danger" style={{ fontSize: '16px', marginBottom: '20px', display: 'block' }}>
            {error}
          </Text>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            size="large"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-news-root">
      <div className="news-header-gradient">
        <Title level={1} className="news-hero-title">
          <FileTextOutlined /> Market News
        </Title>
        <Paragraph className="news-hero-subtitle">
          Stay updated with the latest financial and business news
        </Paragraph>
      </div>
      <div className="news-searchbar-row">
        <Input
          className="news-searchbar-elegant"
          size="middle"
          placeholder="Search news by title, company, or description..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={loading}
          style={{ marginLeft: '10px' }}
        >
          Refresh
        </Button>
      </div>

      <Spin spinning={loading} tip="Loading news...">
        <div className="modern-news-dashboard">
          {hero && (
            <div className="news-hero-card-wrapper">
              <Card
                className="news-hero-card"
                hoverable
                onClick={() => onSelectNews(hero)}
                cover={
                  <img
                    alt="news hero"
                    src={hero.imageUrl || placeholderImg}
                    className="news-hero-img"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = placeholderImg;
                    }}
                  />
                }
              >
                <div className="news-card-header">
                  <Tag color="blue">{hero.source}</Tag>
                  <Text type="secondary" className="news-card-date">
                    <CalendarOutlined /> {hero.publishedDate}
                  </Text>
                </div>
                <Title level={2} className="news-hero-title-main">
                  {hero.title}
                </Title>
                <Paragraph className="news-hero-excerpt">
                  {hero.description ?
                    (hero.description.length > 180 ? `${hero.description.slice(0, 180)}...` : hero.description) :
                    'No description available'
                  }
                </Paragraph>
                <div className="news-card-footer">
                  <Text strong>{hero.company}</Text>
                  <Button type="primary" icon={<EyeOutlined />}>
                    Read
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {featured.length > 0 && (
            <div className="news-featured-row">
              {featured.map((item) => (
                <Card
                  key={item.id}
                  className="news-featured-card"
                  hoverable
                  onClick={() => onSelectNews(item)}
                  cover={
                    <img
                      alt="news featured"
                      src={item.imageUrl || placeholderImg}
                      className="news-featured-img"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = placeholderImg;
                      }}
                    />
                  }
                >
                  <div className="news-card-header">
                    <Tag color="blue">{item.source}</Tag>
                    <Text type="secondary" className="news-card-date">
                      <CalendarOutlined /> {item.publishedDate}
                    </Text>
                  </div>
                  <Title level={4} className="news-featured-title">
                    {item.title}
                  </Title>
                  <Paragraph className="news-featured-excerpt">
                    {item.description ?
                      (item.description.length > 90 ? `${item.description.slice(0, 90)}...` : item.description) :
                      'No description available'
                    }
                  </Paragraph>
                  <div className="news-card-footer">
                    <Text strong>{item.company}</Text>
                    <Button type="link" icon={<EyeOutlined />}>
                      Read
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          <div className="news-list-section elegant-news-list">
            <List
              dataSource={rest}
              locale={{ emptyText: "No news found." }}
              renderItem={(item) => (
                <List.Item className="news-list-item">
                  <div
                    className="news-list-item-content"
                    onClick={() => onSelectNews(item)}
                  >
                    <div className="news-list-item-main">
                      <Title level={5} className="news-list-title">
                        {item.title}
                      </Title>
                      <Paragraph className="news-list-excerpt">
                        {item.description ?
                          (item.description.length > 70 ? `${item.description.slice(0, 70)}...` : item.description) :
                          'No description available'
                        }
                      </Paragraph>
                    </div>
                    <div className="news-list-item-meta">
                      <Tag color="blue">{item.source}</Tag>
                      <Text type="secondary" className="news-card-date">
                        <CalendarOutlined /> {item.publishedDate}
                      </Text>
                      <Text strong>{item.company}</Text>
                      <Button type="link" icon={<EyeOutlined />}>
                        Read
                      </Button>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>

          {/* Pagination */}
          {newsData.totalPages > 1 && (
            <div style={{
              textAlign: 'center',
              marginTop: '30px',
              padding: '20px',
              backgroundColor: isDark ? '#1f1f1f' : '#f5f5f5',
              borderRadius: '8px'
            }}>
              <Pagination
                current={currentPage}
                total={newsData.totalElements}
                pageSize={pageSize}
                showSizeChanger
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                onChange={handlePageChange}
                onShowSizeChange={handlePageChange}
                pageSizeOptions={['5', '10', '20', '50']}
                size="default"
              />
            </div>
          )}
        </div>
      </Spin>
    </div>
  );
};

export default ModernNewsDashboard;
